import { useEffect, useMemo, useRef, useState } from "react";
import {
  Film,
  Plus,
  Play,
  Pause,
  Square,
  Trash2,
  ChevronUp,
  ChevronDown,
  ImageIcon,
  Video as VideoIcon,
  Volume2,
  Captions,
  Wand2,
  Loader2,
  Download,
  AlertTriangle,
  Upload,
  Mic,
  Sparkles,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";
import TaskBanner from "../components/TaskBanner.jsx";
import { renderFrame } from "../lib/videoRender.js";
import { exportProject, downloadBlob } from "../lib/videoExport.js";
import {
  TTS_SUPPORTED,
  STT_SUPPORTED,
  getVoices,
  speak,
  cancelSpeech,
} from "../lib/webSpeech.js";
import {
  generateFullVideoFromPrompt,
  ttsServer,
  transcribeServer,
} from "../lib/api.js";
import { usePendingTask } from "../hooks/usePendingTask.js";
import SpeechToTextButton, {
  appendTranscript,
} from "../components/SpeechToTextButton.jsx";

// ---------------------------------------------------------------------------
// Data model

const ASPECT_PRESETS = {
  "16:9": { label: "16:9 Landscape", width: 1280, height: 720 },
  "9:16": { label: "9:16 Portrait", width: 720, height: 1280 },
  "1:1": { label: "1:1 Square", width: 1080, height: 1080 },
  "4:3": { label: "4:3", width: 1280, height: 960 },
};

function makeScene(overrides = {}) {
  return {
    id: crypto.randomUUID(),
    durationMs: 4000,
    background: { type: "color", color: "#0a0c14" },
    audio: null, // { src, label, sourceText? }
    captions: { text: "" }, // { text } or { items: [{ start, end, text }] }
    ...overrides,
  };
}

const BG_COLORS = [
  "#0a0c14",
  "#3a5bff",
  "#8b5cf6",
  "#ff2196",
  "#0ea5e9",
  "#22c55e",
  "#f59e0b",
  "#ffffff",
];

const FULL_VIDEO_MIN_SECONDS = 12;
const FULL_VIDEO_MAX_SECONDS = 90;
const FULL_VIDEO_MIN_SCENES = 2;
const FULL_VIDEO_MAX_SCENES = 6;

// ---------------------------------------------------------------------------
// Main page

export default function VideoStudio() {
  const [scenes, setScenes] = useState(() => [
    makeScene({
      background: { type: "color", color: "#3a5bff" },
      durationMs: 3000,
      captions: { text: "Welcome to The Consensus A.I." },
    }),
    makeScene({
      background: { type: "color", color: "#8b5cf6" },
      durationMs: 3000,
      captions: { text: "Built with images, video, and voice." },
    }),
  ]);
  const [selectedId, setSelectedId] = useState(scenes[0]?.id);
  const selected = useMemo(
    () => scenes.find((s) => s.id === selectedId),
    [scenes, selectedId]
  );

  const [aspectKey, setAspectKey] = useState("16:9");
  const { width: projectW, height: projectH } = ASPECT_PRESETS[aspectKey];

  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportError, setExportError] = useState(null);
  const [taskBanner, setTaskBanner] = useState(null);
  const [taskReady, setTaskReady] = useState(false);
  const [promptVideoText, setPromptVideoText] = useState("");
  const [promptVideoStyle, setPromptVideoStyle] = useState(
    "cinematic, clean, modern, high quality"
  );
  const [promptSceneCount, setPromptSceneCount] = useState(4);
  const [promptVideoDuration, setPromptVideoDuration] = useState(30);
  const [promptVideoLoading, setPromptVideoLoading] = useState(false);
  const [promptVideoStatus, setPromptVideoStatus] = useState("");
  const [promptVideoError, setPromptVideoError] = useState(null);
  const [promptVideoResult, setPromptVideoResult] = useState(null);

  const updateScene = (id, patch) =>
    setScenes((arr) =>
      arr.map((s) => (s.id === id ? { ...s, ...patch } : s))
    );

  const addScene = () => {
    const s = makeScene();
    setScenes((arr) => [...arr, s]);
    setSelectedId(s.id);
  };

  const deleteScene = (id) => {
    setScenes((arr) => {
      const next = arr.filter((s) => s.id !== id);
      if (id === selectedId) setSelectedId(next[0]?.id);
      return next;
    });
  };

  const moveScene = (id, dir) => {
    setScenes((arr) => {
      const i = arr.findIndex((s) => s.id === id);
      if (i < 0) return arr;
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const copy = [...arr];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  const totalMs = scenes.reduce((a, s) => a + s.durationMs, 0);

  usePendingTask("/studio", (task) => {
    const caption = task.params?.studioCaption || task.prompt;
    setTaskBanner(task.rawPrompt || task.prompt);
    const intro = makeScene({
      background: { type: "color", color: "#3a5bff" },
      durationMs: 5000,
      captions: { text: caption },
    });
    const outro = makeScene({
      background: { type: "color", color: "#8b5cf6" },
      durationMs: 3000,
      captions: { text: "Made with The Consensus A.I." },
    });
    setScenes([intro, outro]);
    setSelectedId(intro.id);
    setTaskReady(true);
  });

  const doExport = async () => {
    setExporting(true);
    setExportProgress(0);
    setExportError(null);
    try {
      const blob = await exportProject(
        { width: projectW, height: projectH, scenes },
        { onProgress: setExportProgress }
      );
      downloadBlob(blob, `consensus-studio-${Date.now()}.mp4`);
    } catch (e) {
      setExportError(e.message);
    } finally {
      setExporting(false);
    }
  };

  const generatePromptVideo = async () => {
    const prompt = promptVideoText.trim();
    if (!prompt || promptVideoLoading) return;
    setPromptVideoLoading(true);
    setPromptVideoError(null);
    setPromptVideoResult(null);
    setPromptVideoStatus("Starting full video generation...");
    try {
      const result = await generateFullVideoFromPrompt(
        {
          prompt,
          style: promptVideoStyle,
          aspect: aspectKey,
          sceneCount: Math.max(
            FULL_VIDEO_MIN_SCENES,
            Math.min(FULL_VIDEO_MAX_SCENES, Number(promptSceneCount) || 4)
          ),
          durationSeconds: Math.max(
            FULL_VIDEO_MIN_SECONDS,
            Math.min(FULL_VIDEO_MAX_SECONDS, Number(promptVideoDuration) || 30)
          ),
          renderVideo: false,
          voiceover: false,
          captions: false,
        },
        {
          onStatus: setPromptVideoStatus,
        }
      );
      const generatedScenes = Array.isArray(result?.assets)
        ? result.assets.map((asset) =>
            makeScene({
              durationMs: Math.max(
                3000,
                Math.round(Number(asset?.plan?.durationSeconds || 5) * 1000)
              ),
              background: asset?.image
                ? { type: "image", src: asset.image }
                : { type: "color", color: "#0a0c14" },
              audio: asset?.audio
                ? {
                    src: asset.audio,
                    label: "AI voiceover",
                    sourceText: asset?.plan?.voiceover || "",
                  }
                : asset?.plan?.voiceover
                  ? {
                      src: null,
                      label: "AI voiceover script",
                      sourceText: asset.plan.voiceover,
                      ttsText: asset.plan.voiceover,
                    }
                  : null,
              captions: { text: asset?.plan?.caption || asset?.plan?.voiceover || "" },
            })
          )
        : [];
      if (generatedScenes.length) {
        setScenes(generatedScenes);
        setSelectedId(generatedScenes[0].id);
      }
      if (result?.aspect && ASPECT_PRESETS[result.aspect]) {
        setAspectKey(result.aspect);
      }
      setPromptVideoResult(result);
      setPromptVideoStatus("Storyboard ready. Generate voiceovers in each scene, then export.");
    } catch (e) {
      setPromptVideoError(e.message || "Failed to generate full video.");
    } finally {
      setPromptVideoLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader
        compact
        icon={Film}
        title="Video Studio"
        subtitle="Arrange images, video, and audio into scenes. Add AI voiceovers and auto-captions, then export."
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-white/60">
              <span className="hidden sm:inline">Aspect</span>
              <select
                value={aspectKey}
                onChange={(e) => setAspectKey(e.target.value)}
                className="input !w-auto !py-1.5 text-xs"
              >
                {Object.entries(ASPECT_PRESETS).map(([key, { label }]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <span className="text-xs text-white/50 hidden sm:inline">
              {scenes.length} scenes · {(totalMs / 1000).toFixed(1)}s total
            </span>
            <button
              onClick={doExport}
              disabled={exporting || !scenes.length}
              className="btn-primary !py-2"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Exporting {Math.round(exportProgress * 100)}%
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Export video
                </>
              )}
            </button>
          </div>
        }
      />

      {taskBanner && (
        <TaskBanner label="Video Studio" prompt={taskBanner} completed={taskReady} />
      )}

      {exportError && (
        <div className="mx-4 md:mx-10 mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 flex gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{exportError}</span>
        </div>
      )}

      <div className="mx-4 md:mx-10 mt-3 card space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-consensus-300" />
              Generate full video from a prompt
            </h2>
            <p className="text-xs text-white/55 mt-1">
              Creates scene images, voiceover scripts, and editable captions, then
              loads the scenes into the studio. Use each scene's built-in AI
              voiceover tool and studio export for the final video. AI full videos are limited to{" "}
              {FULL_VIDEO_MIN_SECONDS}-{FULL_VIDEO_MAX_SECONDS} seconds.
            </p>
          </div>
          {promptVideoResult?.video && (
            <a
              href={promptVideoResult.video}
              download={`consensus-ai-full-video-${Date.now()}.mp4`}
              className="btn-secondary !py-1.5 text-xs"
            >
              <Download className="h-3.5 w-3.5" />
              Download generated MP4
            </a>
          )}
        </div>

        <div className="grid lg:grid-cols-[1fr,260px] gap-3">
          <div className="space-y-2">
            <textarea
              value={promptVideoText}
              onChange={(e) => setPromptVideoText(e.target.value)}
              placeholder="Describe the full video you want. Example: Create a 30-second launch video for a new AI fitness app..."
              className="textarea min-h-[88px]"
            />
            <SpeechToTextButton
              onTranscript={(text) =>
                setPromptVideoText((prev) => appendTranscript(prev, text))
              }
              onError={(msg) => setPromptVideoError(msg)}
            />
            <input
              value={promptVideoStyle}
              onChange={(e) => setPromptVideoStyle(e.target.value)}
              className="input"
              placeholder="Visual style, e.g. cinematic, realistic, bright social ad"
            />
          </div>
          <div className="space-y-2">
            <label className="label">Scenes</label>
            <input
              type="number"
              min={FULL_VIDEO_MIN_SCENES}
              max={FULL_VIDEO_MAX_SCENES}
              value={promptSceneCount}
              onChange={(e) =>
                setPromptSceneCount(
                  Math.max(
                    FULL_VIDEO_MIN_SCENES,
                    Math.min(FULL_VIDEO_MAX_SCENES, Number(e.target.value) || 4)
                  )
                )
              }
              className="input"
            />
            <label className="label">Target duration seconds</label>
            <input
              type="number"
              min={FULL_VIDEO_MIN_SECONDS}
              max={FULL_VIDEO_MAX_SECONDS}
              value={promptVideoDuration}
              onChange={(e) =>
                setPromptVideoDuration(
                  Math.max(
                    FULL_VIDEO_MIN_SECONDS,
                    Math.min(FULL_VIDEO_MAX_SECONDS, Number(e.target.value) || 30)
                  )
                )
              }
              className="input"
            />
            <p className="text-[11px] text-white/45 leading-snug">
              Limit: {FULL_VIDEO_MIN_SCENES}-{FULL_VIDEO_MAX_SCENES} scenes and{" "}
              {FULL_VIDEO_MIN_SECONDS}-{FULL_VIDEO_MAX_SECONDS} seconds per generated video.
            </p>
            <button
              onClick={generatePromptVideo}
              disabled={promptVideoLoading || !promptVideoText.trim()}
              className="btn-primary w-full"
            >
              {promptVideoLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              {promptVideoLoading ? "Generating..." : "Generate full video"}
            </button>
          </div>
        </div>

        {(promptVideoStatus || promptVideoError) && (
          <div
            className={[
              "text-xs rounded-lg border px-3 py-2",
              promptVideoError
                ? "text-red-300 bg-red-500/10 border-red-500/30"
                : "text-white/65 bg-white/[0.03] border-white/10",
            ].join(" ")}
          >
            {promptVideoError || promptVideoStatus}
          </div>
        )}

        {promptVideoResult?.video && (
          <video
            src={promptVideoResult.video}
            controls
            className="w-full max-h-[420px] rounded-xl border border-white/10 bg-black"
          />
        )}
      </div>

      {/* Top: preview + scene editor */}
      <div className="flex-1 grid lg:grid-cols-[1fr,380px] gap-4 px-4 md:px-10 py-4 min-h-0">
        <div className="flex flex-col gap-4 min-h-0">
          <PreviewPlayer
            scenes={scenes}
            selectedId={selectedId}
            width={projectW}
            height={projectH}
          />
        </div>
        <SceneEditor
          scene={selected}
          updateScene={updateScene}
          disabled={!selected}
        />
      </div>

      {/* Bottom: timeline */}
      <Timeline
        scenes={scenes}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onAdd={addScene}
        onDelete={deleteScene}
        onMove={moveScene}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview player — paints scenes to a canvas, plays audio in sync

function PreviewPlayer({ scenes, selectedId, width, height }) {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const ttsTimerRef = useRef(null);
  const stateRef = useRef({ idx: 0, sceneT0: 0, playing: false });
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentIdx, setCurrentIdx] = useState(0);

  const totalMs = scenes.reduce((a, s) => a + s.durationMs, 0);

  // Preload backgrounds (images/videos) so we have something to draw.
  const [assets, setAssets] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const map = {};
      for (const s of scenes) {
        if (s.background?.type === "image" && s.background.src) {
          map[s.id] = await loadImg(s.background.src).catch(() => null);
        } else if (s.background?.type === "video" && s.background.src) {
          map[s.id] = await loadVid(s.background.src).catch(() => null);
        }
      }
      if (!cancelled) setAssets(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [scenes]);

  const previewScene =
    scenes.find((s) => s.id === selectedId) || scenes[0];

  // Paint the currently-selected scene as a static frame when not playing.
  useEffect(() => {
    if (playing) return;
    paintScene(previewScene, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewScene, assets, playing, width, height]);

  const paintScene = (scene, t) => {
    if (!scene) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    const bg = scene.background?.type === "color"
      ? { type: "color", color: scene.background.color }
      : scene.background?.type === "image"
      ? { type: "image", img: assets[scene.id] }
      : scene.background?.type === "video"
      ? { type: "video", video: assets[scene.id] }
      : { type: "color", color: "#000" };
    renderFrame(ctx, {
      width,
      height,
      time: t,
      duration: scene.durationMs / 1000,
      background: bg,
      captions: scene.captions,
    });
  };

  const stop = () => {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(ttsTimerRef.current);
    cancelSpeech();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    stateRef.current.playing = false;
    setPlaying(false);
    setProgress(0);
    setCurrentIdx(0);
    paintScene(previewScene, 0);
  };

  const playFrom = (idx) => {
    const scene = scenes[idx];
    if (!scene) return stop();
    stateRef.current.idx = idx;
    stateRef.current.sceneT0 = performance.now();
    setCurrentIdx(idx);

    // Start audio for this scene (file or TTS)
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (scene.audio?.src) {
      audioRef.current = new Audio(scene.audio.src);
      audioRef.current.play().catch(() => {});
    } else if (scene.audio?.ttsText && TTS_SUPPORTED) {
      // Use browser TTS as a live voiceover when no file is attached
      speak(scene.audio.ttsText, { rate: 1 }).catch(() => {});
    }

    const tick = () => {
      if (!stateRef.current.playing) return;
      const sceneT = performance.now() - stateRef.current.sceneT0;
      paintScene(scene, sceneT / 1000);

      const elapsedBefore = scenes
        .slice(0, idx)
        .reduce((a, s) => a + s.durationMs, 0);
      setProgress((elapsedBefore + sceneT) / totalMs);

      if (sceneT >= scene.durationMs) {
        if (idx + 1 < scenes.length) {
          playFrom(idx + 1);
        } else {
          stop();
        }
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const play = () => {
    if (!scenes.length) return;
    stateRef.current.playing = true;
    setPlaying(true);
    playFrom(0);
  };

  useEffect(() => () => stop(), []); // cleanup on unmount

  return (
    <div className="flex flex-col gap-3 min-h-0 flex-1">
      <div className="relative card p-0 overflow-hidden bg-black flex-1 min-h-0 flex items-center justify-center">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="max-w-full max-h-full w-auto h-auto"
          style={{ aspectRatio: `${width} / ${height}` }}
        />
      </div>
      {/* Transport */}
      <div className="card !p-3 flex items-center gap-3">
        {playing ? (
          <button onClick={stop} className="btn-secondary !py-1.5">
            <Square className="h-3.5 w-3.5 fill-current" />
            Stop
          </button>
        ) : (
          <button
            onClick={play}
            disabled={!scenes.length}
            className="btn-primary !py-1.5"
          >
            <Play className="h-3.5 w-3.5 fill-current" />
            Play
          </button>
        )}
        <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full bg-consensus-500 transition-[width]"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
        <div className="text-[11px] text-white/50 tabular-nums">
          {Math.round(progress * (totalMs / 1000) * 10) / 10}s /{" "}
          {(totalMs / 1000).toFixed(1)}s
        </div>
      </div>
    </div>
  );
}

function loadImg(src) {
  return new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
}
function loadVid(src) {
  return new Promise((res, rej) => {
    const v = document.createElement("video");
    v.crossOrigin = "anonymous";
    v.muted = true;
    v.playsInline = true;
    v.src = src;
    v.onloadeddata = () => {
      v.currentTime = 0;
      v.play().catch(() => {});
      res(v);
    };
    v.onerror = rej;
  });
}

// ---------------------------------------------------------------------------
// Scene editor sidebar

function SceneEditor({ scene, updateScene, disabled }) {
  if (disabled || !scene) {
    return (
      <aside className="card text-sm text-white/40 flex items-center justify-center text-center">
        Select a scene below to edit it.
      </aside>
    );
  }

  return (
    <aside className="card space-y-4 overflow-y-auto">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-tight">Scene</h2>
        <div className="text-[10px] text-white/40 font-mono">
          {scene.id.slice(0, 6)}
        </div>
      </div>

      {/* Background */}
      <BackgroundEditor scene={scene} updateScene={updateScene} />

      {/* Audio */}
      <AudioEditor scene={scene} updateScene={updateScene} />

      {/* Captions */}
      <CaptionsEditor scene={scene} updateScene={updateScene} />

      {/* Duration */}
      <div>
        <label className="label flex items-center justify-between">
          <span>Duration</span>
          <span className="text-white/60 normal-case tracking-normal font-normal">
            {(scene.durationMs / 1000).toFixed(1)}s
          </span>
        </label>
        <input
          type="range"
          min={500}
          max={15000}
          step={100}
          value={scene.durationMs}
          onChange={(e) =>
            updateScene(scene.id, { durationMs: Number(e.target.value) })
          }
          className="w-full accent-consensus-500"
        />
      </div>
    </aside>
  );
}

function BackgroundEditor({ scene, updateScene }) {
  const onUploadImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () =>
      updateScene(scene.id, {
        background: { type: "image", src: r.result },
      });
    r.readAsDataURL(f);
  };
  const onUploadVideo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    updateScene(scene.id, { background: { type: "video", src: url } });
  };

  return (
    <div>
      <label className="label">Background</label>
      <div className="flex items-center gap-1 rounded-lg bg-ink-800/80 border border-white/10 p-1 text-[11px] mb-2">
        {[
          { id: "color", label: "Color" },
          { id: "image", label: "Image" },
          { id: "video", label: "Video" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() =>
              updateScene(scene.id, {
                background:
                  t.id === "color"
                    ? { type: "color", color: scene.background.color || "#000" }
                    : { type: t.id, src: "" },
              })
            }
            className={[
              "flex-1 rounded-md px-2 py-1 transition",
              scene.background.type === t.id
                ? "bg-consensus-500 text-white"
                : "text-white/60 hover:text-white",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      {scene.background.type === "color" && (
        <div className="grid grid-cols-8 gap-1.5">
          {BG_COLORS.map((c) => (
            <button
              key={c}
              onClick={() =>
                updateScene(scene.id, { background: { type: "color", color: c } })
              }
              className={[
                "aspect-square rounded-md border",
                scene.background.color === c
                  ? "border-white"
                  : "border-white/10 hover:border-white/30",
              ].join(" ")}
              style={{ background: c }}
            />
          ))}
        </div>
      )}

      {scene.background.type === "image" &&
        (scene.background.src ? (
          <div className="relative rounded-md overflow-hidden border border-white/10">
            <img src={scene.background.src} alt="" className="w-full" />
            <button
              onClick={() =>
                updateScene(scene.id, { background: { type: "image", src: "" } })
              }
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center text-xs"
            >
              ×
            </button>
          </div>
        ) : (
          <label className="flex items-center justify-center gap-2 border border-dashed border-white/15 rounded-md p-3 text-xs text-white/60 cursor-pointer hover:bg-white/[0.03]">
            <ImageIcon className="h-3.5 w-3.5" />
            Upload image
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onUploadImage}
            />
          </label>
        ))}

      {scene.background.type === "video" &&
        (scene.background.src ? (
          <div className="relative rounded-md overflow-hidden border border-white/10">
            <video src={scene.background.src} className="w-full" muted />
            <button
              onClick={() =>
                updateScene(scene.id, { background: { type: "video", src: "" } })
              }
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center text-xs"
            >
              ×
            </button>
          </div>
        ) : (
          <label className="flex items-center justify-center gap-2 border border-dashed border-white/15 rounded-md p-3 text-xs text-white/60 cursor-pointer hover:bg-white/[0.03]">
            <VideoIcon className="h-3.5 w-3.5" />
            Upload video
            <input
              type="file"
              accept="video/*"
              className="hidden"
              onChange={onUploadVideo}
            />
          </label>
        ))}
    </div>
  );
}

function AudioEditor({ scene, updateScene }) {
  const [ttsText, setTtsText] = useState("");
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsError, setTtsError] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState("");

  useEffect(() => {
    getVoices().then((v) => {
      setVoices(v);
      setVoiceURI(v.find((x) => x.lang?.startsWith("en"))?.voiceURI || "");
    });
  }, []);

  useEffect(() => {
    setTtsText(scene.audio?.sourceText || scene.audio?.ttsText || "");
  }, [scene.id, scene.audio?.sourceText, scene.audio?.ttsText]);

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    updateScene(scene.id, {
      audio: { src: url, label: f.name },
    });
  };

  const generateTts = async () => {
    if (!ttsText.trim() || ttsLoading) return;
    setTtsLoading(true);
    setTtsError(null);
    try {
      // Try NVIDIA first
      const res = await ttsServer({ text: ttsText, format: "wav" });
      updateScene(scene.id, {
        audio: { src: res.audio, label: "AI voiceover", sourceText: ttsText },
      });
    } catch (e) {
      // Fall back to browser TTS as a "playback only" voiceover
      setTtsError(
        `NVIDIA TTS unavailable (${e.message}). Falling back to in-browser voice — it will play during preview but isn't saved as a file.`
      );
      updateScene(scene.id, {
        audio: { src: null, label: "Browser voiceover", ttsText },
      });
    } finally {
      setTtsLoading(false);
    }
  };

  const previewBrowserVoice = () => {
    if (!ttsText.trim()) return;
    const voice = voices.find((v) => v.voiceURI === voiceURI);
    speak(ttsText, { voice }).catch(() => {});
  };

  return (
    <div>
      <label className="label flex items-center gap-2">
        <Volume2 className="h-3.5 w-3.5" /> Audio
      </label>
      {scene.audio ? (
        <div className="mb-2 flex items-center justify-between text-xs bg-white/[0.03] border border-white/10 rounded-md px-2 py-1.5">
          <span className="truncate text-white/80">
            {scene.audio.label || "Audio attached"}
          </span>
          <button
            onClick={() => updateScene(scene.id, { audio: null })}
            className="text-white/40 hover:text-white"
          >
            ×
          </button>
        </div>
      ) : null}
      {scene.audio?.src && (
        <audio src={scene.audio.src} controls className="w-full mb-2" />
      )}

      <label className="flex items-center justify-center gap-2 border border-dashed border-white/15 rounded-md p-2 text-xs text-white/60 cursor-pointer hover:bg-white/[0.03] mb-2">
        <Upload className="h-3.5 w-3.5" />
        Upload audio file
        <input
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={onUpload}
        />
      </label>

      <div className="rounded-md border border-white/10 bg-white/[0.02] p-2 space-y-2">
        <div className="flex items-center gap-1.5 text-[11px] text-white/60">
          <Sparkles className="h-3 w-3 text-consensus-400" />
          AI Voiceover
        </div>
        <textarea
          value={ttsText}
          onChange={(e) => setTtsText(e.target.value)}
          rows={2}
          placeholder="What should the AI say?"
          className="textarea !min-h-[60px] text-xs"
        />
        <SpeechToTextButton
          className="mt-1"
          onTranscript={(text) =>
            setTtsText((prev) => appendTranscript(prev, text))
          }
          onError={(msg) => setTtsError(msg)}
        />
        {voices.length > 0 && (
          <select
            value={voiceURI}
            onChange={(e) => setVoiceURI(e.target.value)}
            className="input !py-1 text-[11px]"
          >
            {voices.slice(0, 30).map((v) => (
              <option key={v.voiceURI} value={v.voiceURI}>
                {v.name} ({v.lang})
              </option>
            ))}
          </select>
        )}
        <div className="flex gap-1.5">
          <button
            onClick={previewBrowserVoice}
            disabled={!ttsText.trim() || !TTS_SUPPORTED}
            className="btn-secondary !py-1 text-[11px] flex-1"
          >
            <Volume2 className="h-3 w-3" />
            Preview
          </button>
          <button
            onClick={generateTts}
            disabled={!ttsText.trim() || ttsLoading}
            className="btn-primary !py-1 text-[11px] flex-1"
          >
            {ttsLoading ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                …
              </>
            ) : (
              <>
                <Wand2 className="h-3 w-3" />
                Generate
              </>
            )}
          </button>
        </div>
        {ttsError && (
          <div className="text-[10px] text-amber-300/90 leading-snug">
            {ttsError}
          </div>
        )}
      </div>
    </div>
  );
}

function CaptionsEditor({ scene, updateScene }) {
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoError, setAutoError] = useState(null);

  const text =
    typeof scene.captions === "string"
      ? scene.captions
      : scene.captions?.text || "";

  const setText = (t) =>
    updateScene(scene.id, { captions: { text: t } });

  const autoCaption = async () => {
    if (!scene.audio?.src || autoLoading) return;
    if (scene.audio.src.startsWith("blob:")) {
      // Convert blob URL into base64 for upload
      setAutoLoading(true);
      setAutoError(null);
      try {
        const blob = await fetch(scene.audio.src).then((r) => r.blob());
        const b64 = await blobToDataUrl(blob);
        const res = await transcribeServer({ audio: b64 });
        if (res.words && res.words.length) {
          updateScene(scene.id, {
            captions: {
              items: res.words.map((w) => ({
                start: Number(w.start ?? w.start_time ?? 0),
                end: Number(w.end ?? w.end_time ?? 0) || (Number(w.start ?? 0) + 0.5),
                text: String(w.word ?? w.text ?? "").trim(),
              })),
              text: res.text,
            },
          });
        } else {
          updateScene(scene.id, { captions: { text: res.text || "" } });
        }
      } catch (e) {
        setAutoError(e.message);
      } finally {
        setAutoLoading(false);
      }
    } else {
      // Data URL — same path
      setAutoLoading(true);
      setAutoError(null);
      try {
        const res = await transcribeServer({ audio: scene.audio.src });
        updateScene(scene.id, { captions: { text: res.text || "" } });
      } catch (e) {
        setAutoError(e.message);
      } finally {
        setAutoLoading(false);
      }
    }
  };

  return (
    <div>
      <label className="label flex items-center gap-2">
        <Captions className="h-3.5 w-3.5" /> Captions
      </label>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder="Caption text shown over the scene"
        className="textarea !min-h-[60px] text-xs"
      />
      <SpeechToTextButton
        className="mt-1"
        onTranscript={(chunk) => setText(appendTranscript(text, chunk))}
        onError={(msg) => setAutoError(msg)}
      />
      <button
        onClick={autoCaption}
        disabled={!scene.audio?.src || autoLoading}
        className="btn-secondary w-full !py-1.5 mt-2 text-[11px]"
        title={
          !scene.audio?.src
            ? "Add audio to this scene first"
            : "Transcribe with NVIDIA Parakeet"
        }
      >
        {autoLoading ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin" />
            Transcribing…
          </>
        ) : (
          <>
            <Mic className="h-3 w-3" />
            Auto-caption from audio
          </>
        )}
      </button>
      {autoError && (
        <div className="text-[10px] text-amber-300/90 mt-1.5 leading-snug">
          {autoError}
        </div>
      )}
      {!STT_SUPPORTED && (
        <div className="text-[10px] text-white/30 mt-1.5">
          (Uses NVIDIA Parakeet — your browser doesn't have native speech
          recognition.)
        </div>
      )}
    </div>
  );
}

function blobToDataUrl(blob) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onloadend = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(blob);
  });
}

// ---------------------------------------------------------------------------
// Timeline (horizontal scene strip)

function Timeline({
  scenes,
  selectedId,
  onSelect,
  onAdd,
  onDelete,
  onMove,
}) {
  return (
    <div className="border-t border-white/5 bg-ink-900/60 p-3 md:px-6">
      <div className="flex items-center gap-3 overflow-x-auto pb-2">
        {scenes.map((s, i) => (
          <SceneCard
            key={s.id}
            scene={s}
            index={i}
            selected={s.id === selectedId}
            onSelect={() => onSelect(s.id)}
            onDelete={() => onDelete(s.id)}
            onMoveLeft={() => onMove(s.id, -1)}
            onMoveRight={() => onMove(s.id, 1)}
            canMoveLeft={i > 0}
            canMoveRight={i < scenes.length - 1}
          />
        ))}
        <button
          onClick={onAdd}
          className="shrink-0 h-[88px] w-[140px] rounded-xl border border-dashed border-white/15 hover:border-consensus-500/40 hover:bg-consensus-500/5 text-white/50 hover:text-white text-xs flex flex-col items-center justify-center gap-1 transition"
        >
          <Plus className="h-4 w-4" />
          Add scene
        </button>
      </div>
    </div>
  );
}

function SceneCard({
  scene,
  index,
  selected,
  onSelect,
  onDelete,
  onMoveLeft,
  onMoveRight,
  canMoveLeft,
  canMoveRight,
}) {
  const previewStyle =
    scene.background.type === "color"
      ? { background: scene.background.color }
      : {};
  return (
    <div
      onClick={onSelect}
      className={[
        "shrink-0 h-[88px] w-[160px] rounded-xl border overflow-hidden cursor-pointer relative group transition",
        selected
          ? "border-consensus-500/60 ring-2 ring-consensus-500/30"
          : "border-white/10 hover:border-white/25",
      ].join(" ")}
    >
      {scene.background.type === "image" && scene.background.src ? (
        <img
          src={scene.background.src}
          alt=""
          className="h-full w-full object-cover"
        />
      ) : scene.background.type === "video" && scene.background.src ? (
        <video
          src={scene.background.src}
          muted
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="h-full w-full" style={previewStyle} />
      )}

      {/* Overlay info */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent flex flex-col justify-between p-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono bg-black/60 rounded px-1.5 py-0.5">
            #{index + 1}
          </span>
          <div className="flex items-center gap-0.5">
            {scene.audio && (
              <span title="Has audio" className="bg-black/60 rounded-full p-0.5">
                <Volume2 className="h-2.5 w-2.5 text-consensus-300" />
              </span>
            )}
            {(scene.captions?.text || scene.captions?.items?.length) && (
              <span title="Has captions" className="bg-black/60 rounded-full p-0.5">
                <Captions className="h-2.5 w-2.5 text-accent-300" />
              </span>
            )}
          </div>
        </div>
        <div className="text-[10px] text-white/80 font-medium">
          {(scene.durationMs / 1000).toFixed(1)}s
        </div>
      </div>

      {/* Action buttons */}
      <div className="absolute top-1 right-1 flex flex-col opacity-0 group-hover:opacity-100 transition">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMoveLeft();
          }}
          disabled={!canMoveLeft}
          className="h-5 w-5 rounded bg-black/70 text-white/80 hover:text-white disabled:opacity-30 flex items-center justify-center"
        >
          <ChevronUp className="h-3 w-3 -rotate-90" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMoveRight();
          }}
          disabled={!canMoveRight}
          className="h-5 w-5 rounded bg-black/70 text-white/80 hover:text-white disabled:opacity-30 flex items-center justify-center"
        >
          <ChevronDown className="h-3 w-3 -rotate-90" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="h-5 w-5 rounded bg-red-500/80 text-white flex items-center justify-center"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
